import React, { useEffect, useReducer, useRef, useState } from "react";
import UserSelect, { UserOption } from "../../Inputs/UserSelect";
import { Button } from "../Buttons/Button";
import { Icon } from "../Icons/LucideIcons";
import { Body } from "../Typography";
import {
  CollaboratorSection,
  InfoRow,
  InlineLink,
  SearchInputWrapper,
  SearchRow,
  SearchSection,
  ShareFormBody,
} from "./__styles__/ShareForm";
import { CollaboratorList } from "./CollaboratorList";
import { InheritedPermissionRow } from "./InheritedPermissionRow";
import { Collaborator, CollaboratorAction, ShareFormProps } from "./types";

const collaboratorReducer = (
  state: Collaborator[],
  action: CollaboratorAction,
): Collaborator[] => {
  switch (action.type) {
    case "ADD_COLLABORATOR":
      // Don't add if already exists
      if (state.some(c => c.id === action.payload.id)) {
        return state;
      }
      return [...state, action.payload];
    case "REMOVE_COLLABORATOR":
      return state.filter(c => c.id !== action.payload.id);
    case "UPDATE_PERMISSION":
      return state.map(c =>
        c.id === action.payload.id ?
          { ...c, permission: action.payload.permission }
        : c,
      );
    case "SET_COLLABORATORS":
      return action.payload;
    default:
      return state;
  }
};

export const ShareForm = ({
  searchPlaceholder = "Add email address",
  sectionLabel = "Who has access",
  infoText,
  initialCollaborators = [],
  permissionOptions,
  onStateChange,
  buttonText = "Add",
  showRemoveOption = false,
  onRemove,
  includeUsers = true,
  includePublicUsers = true,
  inheritedPermissions,
  onInheritedPermissionGroupClick,
  showRole = false,
}: ShareFormProps) => {
  const [collaborators, dispatch] = useReducer(
    collaboratorReducer,
    initialCollaborators,
  );
  const [selectedUserObjects, setSelectedUserObjects] = useState<UserOption[]>(
    [],
  );
  const [userSelectKey, setUserSelectKey] = useState(0);

  useEffect(() => {
    onStateChange?.(collaborators);
  }, [collaborators, onStateChange]);

  // Track the serialized initial collaborators to detect actual changes
  const prevInitialCollaboratorsRef = useRef<string>("");
  useEffect(() => {
    const serialized = JSON.stringify(initialCollaborators);
    if (serialized !== prevInitialCollaboratorsRef.current) {
      prevInitialCollaboratorsRef.current = serialized;
      dispatch({ type: "SET_COLLABORATORS", payload: initialCollaborators });
    }
  }, [initialCollaborators]);

  const hasSelectedUser = selectedUserObjects.length > 0;

  const handleAddCollaborator = () => {
    if (!hasSelectedUser) return;

    const defaultPermission = permissionOptions[0]?.value ?? "can_view";

    selectedUserObjects.forEach(userOption => {
      const name =
        userOption.firstName && userOption.lastName ?
          `${userOption.firstName} ${userOption.lastName}`
        : userOption.email;

      const newCollaborator: Collaborator = {
        id: userOption.id,
        name,
        description: userOption.email,
        permission: defaultPermission,
        userType: userOption._userType,
        role: userOption.role?.name,
      };

      dispatch({ type: "ADD_COLLABORATOR", payload: newCollaborator });
    });

    // Reset selection by remounting UserSelect
    setSelectedUserObjects([]);
    setUserSelectKey(prev => prev + 1);
  };

  const handlePermissionChange = (
    collaboratorId: string,
    permission: string,
  ) => {
    dispatch({
      type: "UPDATE_PERMISSION",
      payload: { id: collaboratorId, permission },
    });
  };

  const handleRemove = (collaboratorId: string) => {
    dispatch({
      type: "REMOVE_COLLABORATOR",
      payload: { id: collaboratorId },
    });
    onRemove?.(collaboratorId);
  };

  return (
    <ShareFormBody>
      <SearchSection>
        <SearchRow>
          <SearchInputWrapper>
            <UserSelect
              key={userSelectKey}
              name="shareUser"
              value={{ users: [], publicUsers: [] }}
              onChange={() => {}}
              onUserObjectsChange={setSelectedUserObjects}
              placeholder={searchPlaceholder}
              includeUsers={includeUsers}
              includePublicUsers={includePublicUsers}
              showSelectAll={false}
            />
          </SearchInputWrapper>
          <Button
            variant="primary"
            size="large"
            title={buttonText}
            onPress={handleAddCollaborator}
            isDisabled={!hasSelectedUser}
          />
        </SearchRow>
        {infoText && (
          <InfoRow>
            <Icon iconName="users" size={16} color="contentSecondary" />
            <Body type="regular" size="default" color="contentSecondary">
              {infoText.text}
              {infoText.linkText && infoText.linkHref && (
                <>
                  {" "}
                  <InlineLink
                    href={infoText.linkHref}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {infoText.linkText}
                  </InlineLink>
                </>
              )}
            </Body>
          </InfoRow>
        )}
      </SearchSection>

      {(collaborators.length > 0 ||
        (inheritedPermissions && inheritedPermissions.length > 0)) && (
        <CollaboratorSection>
          <Body type="emphasis" size="small" color="contentPrimary">
            {sectionLabel}
          </Body>
          {inheritedPermissions?.map(group => (
            <InheritedPermissionRow
              key={group.id}
              group={group}
              onClick={onInheritedPermissionGroupClick}
            />
          ))}
          <CollaboratorList
            collaborators={collaborators}
            permissionOptions={permissionOptions}
            onPermissionChange={handlePermissionChange}
            showRemoveOption={showRemoveOption}
            onRemove={handleRemove}
            showRole={showRole}
          />
        </CollaboratorSection>
      )}
    </ShareFormBody>
  );
};
